import { PolicyPage } from './PolicyPage.js';
import type { CurrencyCode } from '../hooks/useCurrency.js';

/**
 * The privacy policy (`docs/landing/privacy-policy.md`), published for the first time.
 *
 * ── ⚠️ Why this did not exist until 2026-08-18 ────────────────────────────────────────────────
 *
 * Five external documents were counsel-cleared on 2026-08-08 and all five are live — the DPA, the
 * trust portal, the security one-pager, AI transparency, incident response. **A privacy policy was
 * not among them**, and `docs/landing/page-structure.md` had specified one in the footer since it
 * was written: *"Footer: privacy policy, DPA on request, security posture, contact."* Specified and
 * absent, the shape this repository keeps finding.
 *
 * ⚠️ **A DPA is not a privacy policy, and substituting one for the other leaves a real gap.** The
 * DPA covers data where the customer is controller and Dedupr is processor. It says nothing about
 * the personal data Dedupr holds **as controller** — the email addresses of people who sign up, the
 * billing identifiers, the session cookie. Nobody had described that anywhere.
 *
 * ── Why it became urgent ─────────────────────────────────────────────────────────────────────
 *
 * Google's OAuth verification requires a privacy policy at a real URL on a domain we have verified,
 * and for Drive scopes it must carry the **Limited Use** disclosure. Without it the Google Drive app
 * cannot leave test-user mode, so no client could connect a Drive.
 *
 * ⚠️ **`counselCleared={false}`, and that is not a placeholder to tidy up.** The chip is a claim
 * about a legal review that has not happened. Every other page here passes `true` because 2026-08-08
 * happened; this one was written ten days later and must not borrow that clearance.
 */
export interface PrivacyPolicyProps {
  ccyCode: CurrencyCode;
  regionLabel: string;
  onCycleCurrency: () => void;
  onGoHome: () => void;
  onGoDocs: () => void;
  onGoConnectorsDocs: () => void;
  onGoPricing: () => void;
  onOpenAudit: () => void;
  onOpenBooking: () => void;
  onToggleHelp: () => void;
  onGoLegal: () => void;
}

/** The three data classes, kept as data so the table cannot drift from the prose around it. */
const HOLD: { data: string; why: string; where: string }[] = [
  { data: 'Email address', why: 'Signing in — a magic link is the only authentication we offer', where: 'Supabase Auth, UK (London)' },
  { data: 'Organisation name, region choice, plan', why: 'Running the account', where: 'The control-plane database, UK (London)' },
  { data: 'Which organisation you belong to, and your role', why: 'Deciding what you may do', where: 'The control plane — as an opaque identity-provider id, never your email' },
  { data: 'Billing identifiers', why: 'Taking payment', where: 'Stripe. We never see or hold card details' },
  { data: 'Audit records of your actions', why: 'Proving who approved a change to your own data', where: 'Your organisation’s own region-pinned data plane' },
];

const ROLES: { you: string; role: string; governed: string }[] = [
  { you: 'A customer’s staff member whose CRM record we process', role: 'Processor, on your employer’s instructions', governed: 'The DPA — your rights are exercised through them' },
  { you: 'A person who signs up, signs in, or is invited to a workspace', role: 'Controller', governed: 'This document' },
  { you: 'A record owner sent a remediation link by email', role: 'Processor — the address came from your employer’s own system', governed: 'The DPA' },
  { you: 'A visitor to dedupr.cloud', role: 'Controller', governed: 'This document' },
];

export function PrivacyPolicy(props: PrivacyPolicyProps) {
  const { onGoLegal, ...shell } = props;
  return (
    <PolicyPage
      {...shell}
      section="Legal"
      title="Privacy policy"
      reviewed="2026-08-18"
      counselCleared={false}
      intro={
        <>
          The <a href="#/dpa">Data Processing Agreement</a> covers the data you connect — your CRM
          records, your document metadata — where <strong>you are the controller and Dedupr is the
          processor</strong>. This policy covers the other half: the personal data Dedupr holds{' '}
          <strong>as controller</strong>, which is a much shorter list — who signed up, who to bill,
          who did what.
        </>
      }
    >
      <h2 id="who">1 · Who we are</h2>
      <p>
        Dedupr, contactable at <a href="mailto:privacy@dedupr.cloud">privacy@dedupr.cloud</a>. We
        have not appointed a Data Protection Officer; GDPR does not require one for processing of
        this nature and scale, and we would rather say so than name a role nobody holds.
      </p>
      <p>
        <strong>Dedupr is pre-launch and has no customers.</strong> Where something below is not yet
        operating, it says so in place rather than in a footnote.
      </p>

      <h2 id="roles">2 · The two roles, and which one applies to you</h2>
      <table className="land-legal__table">
        <thead>
          <tr><th>If you are…</th><th>Our role</th><th>Governed by</th></tr>
        </thead>
        <tbody>
          {ROLES.map((r) => (
            <tr key={r.you}><td>{r.you}</td><td>{r.role}</td><td>{r.governed}</td></tr>
          ))}
        </tbody>
      </table>
      <p>
        ⚠️ <strong>If you are a data subject in a customer’s CRM, we are not your controller</strong>{' '}
        and we cannot act on a request from you directly — we would be acting on personal data
        without the controller’s instruction, which is the opposite of a processor’s obligation.
        Contact the company whose records you are in; §7 describes how we support them.
      </p>

      <h2 id="hold">3 · What we hold as controller, and where</h2>
      <table className="land-legal__table">
        <thead><tr><th>Data</th><th>Why</th><th>Where it lives</th></tr></thead>
        <tbody>
          {HOLD.map((h) => (
            <tr key={h.data}><td>{h.data}</td><td>{h.why}</td><td>{h.where}</td></tr>
          ))}
        </tbody>
      </table>
      <p>
        ⚠️ <strong>The control-plane database holds no direct identifier, by construction and by a
        build-time check.</strong> It stores an opaque user id, a role and a state — never a name or
        an address. That is why our records <em>about</em> accounts can sit in one UK database while
        every customer’s own records stay pinned to the region they chose.
      </p>
      <p>
        ⚠️ <strong>We do not track you across the web.</strong> No advertising pixel, no analytics
        that identifies a person, no profile built for marketing.
      </p>

      <h2 id="cookies">4 · Cookies</h2>
      <p>
        <strong>One cookie, and it is strictly necessary.</strong> A session cookie holding a sign-in
        token, set <code>HttpOnly</code>, <code>Secure</code> and <code>SameSite=Lax</code>, scoped to
        our own domain, so a signed-in tab stays signed in. There is no consent banner because there
        is nothing to consent to — we set no analytics, advertising or third-party cookies.
      </p>

      <h2 id="connected">5 · Data you connect from another system</h2>
      <p>
        Covered by <a href="#/dpa">the DPA</a> rather than repeated here, but four statements belong
        in both places:
      </p>
      <ul>
        <li>
          <strong>Read before write.</strong> Every connector is granted read-only access at connect
          time. Permission to change anything is requested later, per action type, the first time one
          of your people approves an action of that type.
        </li>
        <li>
          <strong>Nothing is changed without a person approving it</strong>, and every change is
          snapshotted first and reversible for thirty days — except permanent deletion, which is
          refused unless explicitly enabled and which says in the confirmation that it cannot be
          undone.
        </li>
        <li><strong>We never sell your data, and we never use it to train a model.</strong></li>
        <li>
          <strong>Your records stay in the region you chose.</strong> Two documented exceptions exist
          and are recorded rather than hidden: transactional email, and the vector index used for
          AI-ready surfaces.
        </li>
      </ul>

      <h2 id="google">6 · Google user data — the Limited Use disclosure</h2>
      <p>
        If you connect Google Drive, Dedupr requests the single scope{' '}
        <code>drive.metadata.readonly</code>: file and folder <strong>names, owners, and
        created/modified timestamps</strong>, and the sharing posture of shared drives.{' '}
        <strong>We never request access to file contents</strong> — the broader{' '}
        <code>drive.readonly</code> scope is deliberately not requested, because Dedupr audits how a
        document estate is organised and has no reason to open a file.
      </p>
      <blockquote>
        <p>
          <strong>
            Dedupr’s use and transfer of information received from Google APIs to any other app will
            adhere to the{' '}
            <a href="https://developers.google.com/terms/api-services-user-data-policy" target="_blank" rel="noopener noreferrer">
              Google API Services User Data Policy
            </a>
            , including the Limited Use requirements.
          </strong>
        </p>
      </blockquote>
      <p>
        Specifically, Google user data is used <strong>only</strong> to provide the
        document-freshness and duplicate features you asked for; it is <strong>not</strong>{' '}
        transferred to others except as needed to provide those features, to comply with law, or as
        part of a merger with notice; it is <strong>not</strong> used for advertising; and{' '}
        <strong>no human reads it</strong> except with your explicit consent, for security purposes,
        to comply with law, or where the data is aggregated and anonymised.
      </p>
      <p>
        You can disconnect Google Drive at any time from <strong>Sources</strong>, which revokes our
        access with Google where Google offers a revocation endpoint and deletes our copy of the
        credential either way. You can also revoke it yourself at{' '}
        <a href="https://myaccount.google.com/permissions" target="_blank" rel="noopener noreferrer">
          myaccount.google.com/permissions
        </a>
        .
      </p>

      <h2 id="rights">7 · Your rights</h2>
      <p>
        For account data we hold as controller — access, correction, erasure, portability, objection,
        and withdrawal of consent — write to{' '}
        <a href="mailto:privacy@dedupr.cloud">privacy@dedupr.cloud</a>. We answer within{' '}
        <strong>30 days</strong> and track the clock from the day the request arrives.
      </p>
      <p>
        For data in a customer’s connected systems the customer is the controller. We support them
        with a tracked request workflow and produce evidence that an erasure actually happened,
        including destruction of the keys that make a snapshot readable.
      </p>
      <p>
        You may complain to your supervisory authority. In the UK that is the ICO; in the EU it is
        the authority where you live or work.
      </p>

      <h2 id="subprocessors">8 · Sub-processors</h2>
      <p>
        The live register is in <a href="#/dpa">the DPA</a> §3 and is generated per customer from
        what each has actually enabled, so a customer using their own AI keys can prove there is no
        Dedupr-side AI sub-processing at all. Today’s baseline: <strong>Cloudflare</strong> (hosting
        and storage), <strong>Supabase</strong> (account database and sign-in, UK),{' '}
        <strong>Stripe</strong> (billing), <strong>Resend</strong> (transactional email), and an{' '}
        <strong>RFC 3161 timestamping authority</strong> for countersigning the audit chain — ⚠️{' '}
        <strong>no timestamping vendor is contracted yet</strong>, so that last row describes an
        intended control rather than a running one.
      </p>

      <h2 id="transfers">9 · International transfers</h2>
      <p>
        Accounts and sign-in are in the <strong>UK</strong>. Each customer’s own records are pinned to
        the region they chose at signup (<strong>EU or US</strong>). Transfers into the UK from the
        EEA rely on the UK’s adequacy status; the transfer mechanism for customer identity data is{' '}
        <strong>under review with counsel</strong> and is not yet settled — stated here rather than
        asserted.
      </p>

      <h2 id="retention">10 · Retention</h2>
      <p>
        Set out per data class in our security architecture and summarised in{' '}
        <a href="#/dpa">the DPA</a>. The headline figures: audit records <strong>2 years</strong>,
        exports and quarantined rows <strong>90 days</strong>, snapshots for the length of the
        rollback window, staged data for as long as a source is connected and <strong>30 days</strong>{' '}
        after it is disconnected. ⚠️ <strong>The retention schedule is pending final legal
        ratification</strong> and may change before first customer.
      </p>

      <h2 id="changes">11 · Changes</h2>
      <p>
        We will post a new version here with a new date, and tell account owners by email where a
        change is material. This is v0.1 and <strong>has not been through legal review</strong>.
      </p>
      <p>
        <button type="button" className="land-btn land-btn--plain land-legal__inline-link" onClick={onGoLegal}>
          Read the Data Processing Agreement →
        </button>
      </p>
    </PolicyPage>
  );
}
